/*
 * media
 * modules.js: import media from '../scripts/media';
*/

import Player from '@vimeo/player';
import defaultConfig from './vimeoConfig.json';

const media = {
  cfg: {
    name: 'Media',
    selectors: {
      root: '.js-media',
      controls: {
        play: '.js-playBtn',
        pause: '.js-pauseBtn',
      }
    },
    classes: {
      isHidden: 'is-hidden',
      isLoaded: 'is-loaded',
      isReady: 'is-ready',
      hasConsented: 'has-consented',
    },
    el: {
      $media: [],
      $players: [],
      $currentPlayer: undefined,
    },
    observerOptions: {
      root: null,
      rootMargin: '100%',
      threshold: 0.1,
    },
    $autoplay: undefined,
  },

  setElements() {
    this.cfg.el.$media = [...document.querySelectorAll(this.cfg.selectors.root)];
  },

  setAutoPlay() {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (WL_DEFAULTS.media.autoplay && reducedMotion.matches) {
      this.cfg.$autoplay = false;
    } else {
      this.cfg.$autoplay = WL_DEFAULTS.media.autoplay;
    }
  },

  handleControlsVisibilty(_controls, _player) {
    const { play, pause } = _controls;

    _player.getPaused().then((paused) => {
      const state = paused ? 'pause' : 'play';

      if(state === 'play') {
        play.classList.add(this.cfg.classes.isHidden);
        pause.classList.remove(this.cfg.classes.isHidden);
      } else if (state === 'pause') {
        play.classList.remove(this.cfg.classes.isHidden);
        pause.classList.add(this.cfg.classes.isHidden);
      }
    });
  },

  play(_player) {
    _player.play().catch(error => {
      console.error('Error playing video:', error);
    });
  },

  pause(_player) {
    _player.pause().catch(error => {
      console.error('Error pausing video:', error);
    });
  },

  pauseAllExcept(currentPlayer, _controls) {
    this.cfg.el.$players.forEach(player => {
      if (player !== currentPlayer) {
        this.pause(player);
      }
    });
  },

  playOnEnter(_item, player) {
    const enterObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && this.cfg.$autoplay) {
          this.play(player);
        }
      });
    });
    enterObserver.observe(_item);
  },

  pauseOnExit(_item, player) {
    const exitObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          this.pause(player);
        }
      });
    });
    exitObserver.observe(_item);
  },

  addPlayer(_container, _config) {
    const player = new Player(_container, _config);
    this.cfg.el.$players.push(player);

    const parent = _container.parentElement;
    const playerControls = {
      play: parent.querySelector(this.cfg.selectors.controls.play),
      pause: parent.querySelector(this.cfg.selectors.controls.pause)
    }

    player.on('play', () => {
      this.cfg.el.$currentPlayer = player;
      this.pauseAllExcept(this.cfg.el.$currentPlayer, playerControls);
      this.handleControlsVisibilty(playerControls, player);
    });

    player.on('pause', () => {
      this.handleControlsVisibilty(playerControls, player);
    });

    player.ready().then(() => {
      _container.classList.add(this.cfg.classes.isReady);
      this.handleControlsVisibilty(playerControls, player);
    });

    // Check if data-vimeo-bg is present
    const background = _container.getAttribute('data-vimeo-bg');
    if (typeof background !== typeof undefined && background !== null) {
      this.pauseOnExit(_container, player);
      this.playOnEnter(_container, player);
    }

    // Play & Pause Control
    playerControls.play.addEventListener('click', (e)  => {
      this.play(player);
    });

    // Play & Pause Control
    playerControls.pause.addEventListener('click', (e)  => {
      this.pause(player);
    });
  },

  initMedia(_item) {
    // assign default vimeo options from: './vimeoConfig.json'
    let config = Object.assign({}, defaultConfig);

    // background options
    const background = _item.getAttribute('data-vimeo-bg');
    if (typeof background !== typeof undefined && background !== null) {
      if (this.cfg.$autoplay) {
        config.background = true;
      } else {
        config.loop = true;
        config.muted = true;
      }
    }
    // ADD NEW OPTIONS HERE
    // CODE

    const vimeoSrc = _item.getAttribute('data-vimeo-src');
    if (typeof vimeoSrc !== typeof undefined && vimeoSrc !== null) {
      // add & init player
      config.url = vimeoSrc;

      if (vimeoSrc.includes('vimeo.com')) {
        this.addPlayer(_item, config);
      }
    }
  },

  loadVimeoOnIntersect(_item) {
    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.initMedia(_item)
          observer.disconnect();
        }
      });
    }, this.cfg.observerOptions);

    observer.observe(_item);
  },

  handleConsent(_cfConset) {
    this.cfg.el.$media.forEach((item) => {
      const cookieConset = _cfConset || window.CookieFirst.consent;
      const cookieType = item.getAttribute('data-cookiefirst-category');

      if (cookieConset[cookieType]) {
        item.classList.add(this.cfg.classes.isLoaded, this.cfg.classes.hasConsented);
        this.loadVimeoOnIntersect(item);
      }
    });
  },

  addEventListener() {
    if (!WL_DEV_MODE) {
      window.addEventListener('cf_init', (e) => {
        if (e.currentTarget.CookieFirst.hasConsented === false) return false;
        console.log(e.currentTarget.CookieFirst.consent);
        this.handleConsent(e.currentTarget.CookieFirst.consent);
      });
      
      window.addEventListener('cf_consent', (e) => {
        if (e.detail === null) return false;
        this.handleConsent(e.detail);
      });
    } else {
      this.handleConsent(WL_DEFAULTS.cookiefirst.dev.categorys);
    };
  },

  init() {
    console.debug(`Init: ${this.cfg.name}`);
    this.setElements();
    this.setAutoPlay();

    if (this.cfg.el.$media) {
      this.addEventListener();
    }
  },
};

export default media;